import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Download,
  FileText,
  Film,
  Image as ImageIcon,
  type LucideIcon,
  Mic,
  Music,
  Paperclip,
  Smile,
  Video,
} from "lucide-react";
import { ApiError } from "@/lib/api";
import { type InboxMessage, fetchMessageMedia, mediaLabel } from "@/lib/inbox";
import { Spinner } from "@/components/ui/spinner";

const ICONS: Record<string, LucideIcon> = {
  photo: ImageIcon,
  video: Video,
  video_note: Video,
  gif: Film,
  voice: Mic,
  audio: Music,
  document: FileText,
  sticker: Smile,
};
// Nothing to open behind these: a sticker is not a file worth a connection of
// the account, and "other" is a contact, a location, a poll.
const NOT_OPENABLE = new Set(["sticker", "other"]);

// What is shown as a picture: only the kinds a browser draws without running
// anything. An SVG can carry a script, and opened in a tab of its own (the
// "full size" link) it would run as a page of ours — so it is saved, like any
// other document a stranger sent.
const PICTURE = /^image\/(png|jpeg|gif|webp|bmp)$/;
const PLAYABLE = /^(audio|video)\//;

type Opened = { url: string; type: string };

/**
 * What a message carries — "Photo", "Voice message", a file's name — and, on a
 * click, the thing itself. The file is not kept on our side: the click fetches
 * it from Telegram through the dialog's account, then a picture, a voice note
 * or a video shows right in the bubble, and anything else is saved to disk.
 */
export function MessageMedia({
  dialogId,
  message,
}: {
  dialogId: string;
  message: Pick<InboxMessage, "id" | "media_kind" | "media_name">;
}) {
  const { t } = useTranslation();
  const [opened, setOpened] = useState<Opened | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(
    () => () => {
      if (opened) URL.revokeObjectURL(opened.url);
    },
    [opened],
  );

  // A message stored before kinds were recorded only says "there was something".
  const kind = message.media_kind ?? "unknown";
  const Icon = ICONS[kind] ?? Paperclip;
  const label = mediaLabel(message, t);
  const fileName = message.media_name ?? t(`inbox.media.kind.${kind}`);

  const open = async () => {
    setError(null);
    setLoading(true);
    try {
      const blob = await fetchMessageMedia(dialogId, message.id);
      const next = { url: URL.createObjectURL(blob), type: blob.type };
      setOpened(next);
      if (!PICTURE.test(next.type) && !PLAYABLE.test(next.type)) save(next.url, fileName);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("inbox.media.failed"));
    } finally {
      setLoading(false);
    }
  };

  const chip = "mb-1 flex max-w-full items-center gap-1.5 text-xs text-fg-muted";
  if (NOT_OPENABLE.has(kind)) {
    return (
      <p className={chip}>
        <Icon size={13} strokeWidth={2} aria-hidden="true" className="shrink-0" />
        <span className="truncate italic">{label}</span>
      </p>
    );
  }

  if (opened && PICTURE.test(opened.type)) {
    return (
      <a href={opened.url} target="_blank" rel="noreferrer" title={t("inbox.media.full_size")}>
        <img src={opened.url} alt={label} className="mb-1 max-h-72 max-w-full rounded-lg" />
      </a>
    );
  }
  if (opened?.type.startsWith("audio/")) {
    return <audio src={opened.url} controls autoPlay className="mb-1 h-10 w-64 max-w-full" />;
  }
  if (opened?.type.startsWith("video/")) {
    return <video src={opened.url} controls className="mb-1 max-h-72 max-w-full rounded-lg" />;
  }

  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={opened ? () => save(opened.url, fileName) : open}
        disabled={loading}
        title={t(opened ? "inbox.media.save" : "inbox.media.open")}
        className="flex max-w-full items-center gap-1.5 rounded-md border border-border bg-bg-primary/40 px-2 py-1 text-left text-xs text-fg-primary transition-colors hover:bg-bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-70"
      >
        {loading ? (
          <Spinner size="sm" />
        ) : opened ? (
          <Download size={13} strokeWidth={2} aria-hidden="true" className="shrink-0" />
        ) : (
          <Icon size={13} strokeWidth={2} aria-hidden="true" className="shrink-0" />
        )}
        <span className="truncate">{loading ? t("inbox.media.loading") : label}</span>
      </button>
      {error && <p className="mt-1 break-words text-xs text-danger">{error}</p>}
    </div>
  );
}
MessageMedia.displayName = "MessageMedia";

/** Hand a fetched file to the browser's "save as". */
function save(url: string, name: string) {
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
}
